import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PageHeader, Card } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CheckCircle2, Ban, Target, MessageSquare, Settings, AlertTriangle, XOctagon } from "lucide-react";

export default function SOPGuide() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/sop-guide").then((r) => setItems(r.data)); }, []);

  return (
    <div>
      <PageHeader title={t("sop")} subtitle="Standard Operating Procedure checklists" />
      {items.length > 0 && (
        <Tabs defaultValue={items[0].key} className="max-w-4xl">
          <TabsList className="mb-6 bg-slate-100 p-1 flex flex-wrap h-auto gap-1">
            {items.map((s) => (
              <TabsTrigger key={s.key} value={s.key} className="data-[state=active]:bg-white data-[state=active]:text-indigo-600 data-[state=active]:shadow-sm">
                {lang === "en" ? s.en_title : s.hi_title}
              </TabsTrigger>
            ))}
          </TabsList>
          
          {items.map((s) => {
            const objective = lang === "en" ? s.objective_en : s.objective_hi;
            const script = lang === "en" ? s.script_en : s.script_hi;
            const procedure = lang === "en" ? s.procedure_en : s.procedure_hi;
            const escalation = lang === "en" ? s.escalation_en : s.escalation_hi;
            const prohibited = lang === "en" ? s.prohibited_en : s.prohibited_hi;
            
            return (
              <TabsContent key={s.key} value={s.key}>
                <Card className="overflow-hidden border-0 shadow-lg">
                  <div className="bg-indigo-600 p-6 text-white">
                    <h3 className="font-head text-2xl font-bold">{lang === "en" ? s.en_title : s.hi_title}</h3>
                  </div>
                  
                  <div className="p-6 space-y-6 bg-slate-50">
                    
                    {/* Primary Objective */}
                    {objective && (
                      <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
                        <div className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">
                          <Target className="h-4 w-4 text-indigo-500" /> Primary Objective
                        </div>
                        <p className="text-[15px] text-slate-800 leading-relaxed">{objective}</p>
                      </div>
                    )}
                    
                    {/* Scripted Responses */}
                    {script && (
                      <div className="bg-indigo-50 p-5 rounded-lg border border-indigo-100 shadow-sm relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500" />
                        <div className="flex items-center gap-2 text-sm font-bold text-indigo-600 uppercase tracking-widest mb-3">
                          <MessageSquare className="h-4 w-4" /> Scripted Response
                        </div>
                        <p className="text-[16px] italic text-indigo-900 leading-relaxed font-medium">"{script}"</p>
                      </div>
                    )}
                    
                    {/* Procedural Execution */}
                    {procedure && procedure.length > 0 && (
                      <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
                        <div className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-widest mb-4">
                          <Settings className="h-4 w-4 text-blue-500" /> Procedural Execution
                        </div>
                        <ul className="space-y-3">
                          {procedure.map((step, i) => (
                            <li key={i} className="flex items-start gap-3 text-[15px] text-slate-700">
                              <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                              {step}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Escalation Triggers */}
                      {escalation && (
                        <div className="bg-amber-50 p-5 rounded-lg border border-amber-200 shadow-sm">
                          <div className="flex items-center gap-2 text-sm font-bold text-amber-600 uppercase tracking-widest mb-3">
                            <AlertTriangle className="h-4 w-4" /> Escalation Trigger
                          </div>
                          <p className="text-[14px] text-amber-900 leading-relaxed font-medium">{escalation}</p>
                        </div>
                      )}
                      
                      {/* Strictly Prohibited */}
                      {prohibited && (
                        <div className="bg-red-50 p-5 rounded-lg border border-red-200 shadow-sm">
                          <div className="flex items-center gap-2 text-sm font-bold text-red-600 uppercase tracking-widest mb-3">
                            <XOctagon className="h-4 w-4" /> Strictly Prohibited
                          </div>
                          <p className="text-[14px] text-red-900 leading-relaxed font-medium">{prohibited}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </TabsContent>
            );
          })}
        </Tabs>
      )}
    </div>
  );
}
